import {
  Box,
  Circle,
  Flex,
  HStack,
  Modal,
  ModalContent,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
  Badge,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import React, { useState, useEffect, useContext } from 'react';
import { CloseIcon, OpenIcon } from '../../constants/IconData';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { FILE_TYPE } from '../../constants/constants';
import { AuditReportContent, AUDIT_REPORT_MIME, useAuditReportAttachment } from './AuditReportViewer';
import { AuditSheetContext } from '../../providers/AuditSheetProvider';

const FILE_TYPE_MAP = {
  'application/CDFV2': 'application/msword',
  'application/vnd.ms-excel': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const EXCEL_EXTENSIONS = ['xls', 'xlsx', 'xlsm', 'xlsb'];

const getFileType = (mimeType, fileName) => {
  if (!fileName) return FILE_TYPE_MAP[mimeType] ?? mimeType;
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (extension === 'csv') return 'text/csv';
  if (extension && EXCEL_EXTENSIONS.includes(extension)) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  return FILE_TYPE_MAP[mimeType] ?? mimeType;
};

const getFileBadge = (fileType) => {
  if (fileType === FILE_TYPE.CLIENT)     return { label: 'Client',     color: 'blue'   };
  if (fileType === FILE_TYPE.AUDIT)      return { label: 'Audited',    color: 'green'  };
  if (fileType === FILE_TYPE.ADDITIONAL) return { label: 'Additional', color: 'purple' };
  if (fileType === 'AUDIT_REPORT')       return { label: 'Report',     color: 'teal'   };
  return null;
};

const createFileEntry = (fileSource, displayFileName, fileMimeType, namePrefix, uriKey, fileType) => {
  if (!fileSource?.[uriKey]) return null;
  const uri = fileSource[uriKey];
  const cleanedName = `${namePrefix}${displayFileName.split('_').join(' ')}`;
  return {
    name:       cleanedName,
    uri,
    fileType:   getFileType(fileMimeType, displayFileName),
    fileName:   displayFileName,
    mimeType:   fileMimeType,
    size:       fileSource.size,
    uploadedAt: fileSource.uploaded_at,
    uploader:   fileSource.uploaded_by
      ? `${fileSource.uploaded_by.first_name} ${fileSource.uploaded_by.last_name}`
      : null,
    type: fileType,
  };
};

const buildFilesList = (currentFile, attachments) => {
  const files = [];

  const originalEntry = createFileEntry(
    currentFile, currentFile.name, currentFile.mime_type, '', 'preview_url', FILE_TYPE.CLIENT
  );
  if (originalEntry) files.push(originalEntry);

  attachments.forEach((attachment) => {
    const f = attachment.file;
    if (f?.mime_type === AUDIT_REPORT_MIME) {
      files.push({
        name:                f.name,
        uri:                 f.url,
        fileType:            AUDIT_REPORT_MIME,
        mimeType:            AUDIT_REPORT_MIME,
        type:                attachment.attachment_type,
        _auditReportData:    f._auditReportData,
        _auditReportLoading: f._auditReportLoading,
        _auditReportError:   f._auditReportError,
        _auditReportRefresh: f._auditReportRefresh,
      });
      return;
    }
    const entry = createFileEntry(f, f.name, f.mime_type, '', 'url', attachment.attachment_type);
    if (entry) files.push(entry);
  });

  const auditedEntry = createFileEntry(
    currentFile,
    currentFile.audited_chart_name,
    currentFile.audited_chart_mime_type,
    '',
    'audited_chart_preview_url',
    FILE_TYPE.AUDIT
  );
  if (auditedEntry) files.push(auditedEntry);

  return files;
};

// ─── Per-chart hook wrapper ───────────────────────────────────────────────────

const ChartFilesLoader = ({ chart, onFilesReady }) => {
  const { attachment } = useAuditReportAttachment({ chartId: chart.id });

  useEffect(() => {
    if (!chart?.file_obj) { onFilesReady([]); return; }
    const mergedAttachments = [
      ...(chart.attachments ?? []),
      ...(attachment ? [attachment] : []),
    ];
    onFilesReady(buildFilesList(chart.file_obj, mergedAttachments));
  }, [attachment, chart]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

// ─── BatchFileViewer ──────────────────────────────────────────────────────────

const BatchFileViewer = ({ isOpen, onClose, batchFiles = [] }) => {
  const [expandedCharts, setExpandedCharts] = useState([0]);
  const [selectedFlatIndex, setSelectedFlatIndex] = useState(null); // null = nothing selected yet
  const [allFiles, setAllFiles]   = useState([]);
  const [indexMap, setIndexMap]   = useState([]);
  const [chartFilesMap, setChartFilesMap] = useState({});
  const auditSheetContext = useContext(AuditSheetContext);
  const {
    isFileLoading
  } = auditSheetContext;

  // Rebuild flat list whenever any chart's files resolve
  useEffect(() => {
    if (!isOpen || batchFiles.length === 0) return;

    const files = [];
    const map = batchFiles.map((_, chartIndex) => {
      const chartFiles = chartFilesMap[chartIndex] ?? [];
      const start = files.length;
      files.push(...chartFiles);
      return chartFiles.map((_, i) => start + i);
    });

    setAllFiles(files);
    setIndexMap(map);
  }, [isOpen, batchFiles, chartFilesMap]);

  // FIX 1: Once the first chart's files arrive and nothing is selected yet, auto-select index 0
  useEffect(() => {
    if (allFiles.length > 0 && selectedFlatIndex === null) {
      setSelectedFlatIndex(0);
    }
  }, [allFiles, selectedFlatIndex]);

  // Reset everything when modal opens or batch changes
  useEffect(() => {
    if (isOpen) {
      setExpandedCharts([0]);
      setSelectedFlatIndex(null); // null so auto-select above fires once files arrive
      setChartFilesMap({});
      setAllFiles([]);
      setIndexMap([]);
    }
  }, [isOpen, batchFiles]);

  const handleChartFilesReady = (chartIndex, files) => {
    setChartFilesMap((prev) => {
      if (JSON.stringify(prev[chartIndex]) === JSON.stringify(files)) return prev;
      return { ...prev, [chartIndex]: files };
    });
  };

  // FIX 2: When expanding a chart, auto-select its first file
  const toggleChart = (chartIndex) => {
    setExpandedCharts((prev) => {
      const isCurrentlyExpanded = prev.includes(chartIndex);
      if (isCurrentlyExpanded) {
        // Collapsing — just close it
        return prev.filter((i) => i !== chartIndex);
      } else {
        // Expanding — open it and select its first file
        const firstFlatIndex = indexMap[chartIndex]?.[0];
        if (firstFlatIndex !== undefined) {
          setSelectedFlatIndex(firstFlatIndex);
        }
        return [...prev, chartIndex];
      }
    });
  };

  const selectedFile  = selectedFlatIndex !== null ? allFiles[selectedFlatIndex] : null;
  const isAuditReport = selectedFile?.mimeType === AUDIT_REPORT_MIME;
  const docs = (!selectedFile || isAuditReport)
    ? []
    : [{ uri: selectedFile.uri, fileType: selectedFile.fileType }];

  return (
    <Modal size="full" onClose={onClose} isOpen={isOpen}>
      <ModalOverlay bg="#001A41" opacity={0.8} />
      <ModalContent
        bg="white"
        maxW="90vw"
        maxH="100vh"
        minH="100vh"
        borderRadius="lg"
        overflow="hidden"
        boxShadow="2xl"
        display="flex"
        flexDirection="column"
      >
        {isFileLoading ? (
          <Flex h="100%" align="center" justify="center">
            <Text>Loading files...</Text>
            <Spinner />
          </Flex>
        ) : (
          <>
        {/* Invisible loaders — one per chart */}
        {isOpen && batchFiles.map((chart, chartIndex) => (
          <ChartFilesLoader
            key={chart.id ?? chartIndex}
            chart={chart}
            onFilesReady={(files) => handleChartFilesReady(chartIndex, files)}
          />
        ))}

        {/* Header */}
        <HStack px={4} py={3} borderBottom="1px" borderColor="gray.200" bg="white" flexShrink={0}>
          <Text fontSize="lg" fontWeight="semibold" color="gray.800" noOfLines={1}>
            {selectedFile?.name || 'Batch File Viewer'}
          </Text>
          <Spacer />
          {selectedFile && !isAuditReport && (
            <Circle
              size="36px"
              bg="blue.50"
              color="blue.600"
              onClick={() => window.open(selectedFile.uri, '_blank')}
              cursor="pointer"
              _hover={{ bg: 'blue.100' }}
              transition="all 0.2s"
              title="Open in new tab"
            >
              <OpenIcon />
            </Circle>
          )}
          <Circle
            size="36px"
            bg="gray.100"
            color="gray.600"
            cursor="pointer"
            onClick={onClose}
            _hover={{ bg: 'gray.200' }}
            transition="all 0.2s"
            title="Close"
          >
            <CloseIcon height="16px" width="16px" />
          </Circle>
        </HStack>

        <Flex flex="1" overflow="hidden" minH="0">
          {/* Sidebar */}
          <VStack
            w="280px"
            bg="gray.50"
            p={3}
            spacing={1}
            align="stretch"
            borderRight="1px"
            borderColor="gray.200"
            overflowY="auto"
            flexShrink={0}
          >
            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>
              CHARTS ({batchFiles.length})
            </Text>

            {batchFiles.map((chart, chartIndex) => {
              const isExpanded    = expandedCharts.includes(chartIndex);
              const chartFlatIdxs = indexMap[chartIndex] ?? [];
              const chartFiles    = chartFlatIdxs.map((fi) => allFiles[fi]);
              const chartLabel    = chart.file_id ?? `Chart ${chartIndex + 1}`;
              const chartFileName = chart.file_obj?.name ?? '';

              return (
                <Box key={chart.id ?? chartIndex}>
                  <HStack
                    px={3}
                    py={2}
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                    cursor="pointer"
                    onClick={() => toggleChart(chartIndex)}
                    _hover={{ bg: 'gray.100', borderColor: 'gray.300' }}
                    transition="all 0.2s"
                    spacing={2}
                  >
                    <Box color="gray.500" flexShrink={0}>
                      {isExpanded
                        ? <ChevronDownIcon boxSize={4} />
                        : <ChevronRightIcon boxSize={4} />
                      }
                    </Box>
                    <Box flex="1" minW="0">
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700" noOfLines={1}>
                        {chartLabel}
                      </Text>
                      {chartFileName ? (
                        <Text fontSize="xs" color="gray.400" noOfLines={1}>
                          {chartFileName}
                        </Text>
                      ) : null}
                    </Box>
                    <Badge fontSize="xs" colorScheme="gray" flexShrink={0}>
                      {chartFiles.length}
                    </Badge>
                  </HStack>

                  {isExpanded && (
                    <VStack spacing={1} align="stretch" mt={1} pl={4}>
                      {chartFiles.length === 0 && (
                        <Text fontSize="xs" color="gray.400" px={2} py={1}>
                          No files
                        </Text>
                      )}
                      {chartFiles.map((file, fileIndex) => {
                        const flatIndex  = chartFlatIdxs[fileIndex];
                        const isSelected = selectedFlatIndex === flatIndex;
                        const badge      = getFileBadge(file?.type);

                        return (
                          <Box
                            key={fileIndex}
                            p={3}
                            bg={isSelected ? 'blue.50' : 'white'}
                            border="1px"
                            borderColor={isSelected ? 'blue.200' : 'gray.200'}
                            borderRadius="md"
                            cursor="pointer"
                            onClick={() => setSelectedFlatIndex(flatIndex)}
                            _hover={{
                              bg: isSelected ? 'blue.50' : 'gray.50',
                              borderColor: isSelected ? 'blue.300' : 'gray.300',
                            }}
                            transition="all 0.2s"
                          >
                            <Text
                              fontWeight={isSelected ? 'semibold' : 'medium'}
                              fontSize="sm"
                              color="gray.700"
                              noOfLines={2}
                              mb={badge ? 1 : 0}
                            >
                              {file?.name}
                            </Text>
                            {badge && (
                              <Badge colorScheme={badge.color} fontSize="xs">
                                {badge.label}
                              </Badge>
                            )}
                          </Box>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              );
            })}
          </VStack>

          {/* Main viewer */}
          <Box
            flex="1"
            bg={isAuditReport ? 'white' : 'gray.100'}
            position="relative"
            display="flex"
            flexDirection="column"
            minHeight="0"
            sx={{
              '& #react-doc-viewer': { height: '100% !important', display: 'flex', flexDirection: 'column' },
              '& #proxy-renderer':   { height: '100% !important', display: 'flex', flexDirection: 'column' },
              '& #msdoc-renderer':   { height: '100% !important', display: 'flex', flexDirection: 'column' },
              '& #msdoc-iframe':     { height: '100% !important', minHeight: '100vh' },
              '& iframe':            { height: '100% !important' },
            }}
          >
            {isAuditReport ? (
              <Box flex="1" overflow="auto" minHeight="0">
                <AuditReportContent
                  data={selectedFile._auditReportData}
                  loading={selectedFile._auditReportLoading}
                  error={selectedFile._auditReportError}
                  onRefresh={selectedFile._auditReportRefresh}
                />
              </Box>
            ) : docs.length > 0 ? (
              <Box p={2} flex="1" overflow="auto" minHeight="0">
                <DocViewer
                  key={selectedFlatIndex}
                  documents={docs}
                  pluginRenderers={DocViewerRenderers}
                  config={{
                    pdfZoom: { defaultZoom: 1.0, zoomJump: 0.2 },
                    header: { disableHeader: true, disableFileName: false, retainURLParams: false },
                    pdfVerticalScrollByDefault: true,
                    csvDelimiter: ',',
                  }}
                  style={{ borderRadius: '8px' }}
                />
              </Box>
            ) : (
              <Flex h="100%" align="center" justify="center" direction="column" color="gray.500">
                <Text>
                  {allFiles.length === 0 ? 'Loading files...' : 'Select a file to view'}
                </Text>
              </Flex>
            )}
          </Box>
        </Flex>
        </>)}
      </ModalContent>
    </Modal>
  );
};

export default BatchFileViewer;